import {
  Box,
  Button,
  Divider,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import api from "../../../../services/api";
import { useNotification } from "../../../../componentes/notificationProvider";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/info.svg";
import { AxiosError } from "axios";
import useStyles from "./styles";
import { Storage } from "@mui/icons-material";
import QueryBox from "../../../../componentes/filters/filterQuery";
import { RowsDataSource } from "../../../../componentes/administracao/types";

type FilterType = "select" | "date";
interface Filter {
  type: FilterType;
  label: string;
  column: string;
  variable: string;
  query?: string;
  datasource?: { id: string };
}
interface FiltersResponse {
  filters: Filter[];
}
type OutletContextType = {
  setIsFilterEditor: React.Dispatch<React.SetStateAction<boolean>>;
};
export default function AddFilter() {
  const theme = useTheme();
  const styles = useStyles(theme);
  const notify = useNotification();
  const navigate = useNavigate();
  const { pageId, variableName } = useParams();
  const { setIsFilterEditor } = useOutletContext<OutletContextType>();

  const [pageFilter, setPageFilter] = useState<Filter[]>([]);
  const [variable, setVariable] = useState<string>("");
  const [filterType, setFilterType] = useState<FilterType | null>(null);
  const [label, setLabel] = useState<string>("");
  const [column, setColumn] = useState<string>("");
  const [query, setQuery] = useState<string>("");
  const [resultQuery, setResultQuery] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorQuery, setErrorQuery] = useState<string>("");
  const [filterTable, setFilterTable] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [selectFilterIndex, setSelectedFilterIndex] = useState<number>();
  const [fieldErrors, setFieldErrors] = useState({
    variable: false,
    label: false,
    column: false,
    query: false,
  });
  const [tables, setTables] = useState<
    Record<string, { columnName: string; type: string }[]>
  >({});
  const [datasource, setDatasource] = useState<{ id: string } | null>(null);
  const [dataSources, setDataSources] = useState<RowsDataSource[]>([]);

  useEffect(() => {
    const fetchFilters = async () => {
      if (!pageId) return;
      try {
        const response = await api.post<FiltersResponse>(
          "/filters/findFilters",
          {
            id: pageId,
          },
        );
        setPageFilter(response.data.filters);
        const filterIndex = response.data.filters.findIndex(
          (filter) => filter.variable === variableName,
        );
        if (filterIndex !== -1) {
          setSelectedFilterIndex(filterIndex);
          const filter = response.data.filters[filterIndex];
          setVariable(filter.variable);
          setFilterType(filter.type);
          setLabel(filter.label);
          setColumn(filter.column);
          setQuery(filter.query || "");
          setDatasource(filter.datasource || null);
        }
      } catch (error) {
        if (error instanceof AxiosError)
          notify(
            error.response?.status || 500,
            error.response?.data.error,
            "error",
            null,
          );
        else notify("Não foi possível se conectar ao servidor!", "error");
      }
    };
    fetchFilters();
  }, [pageId]);

  useEffect(() => {
    if (!datasource) return;
    async function fetchTables() {
      try {
        const result = await api.post("/editor/tables", {
          id: datasource!.id,
        });
        setTables(result.data);
      } catch (error) {
        console.error(error);
        if (error instanceof AxiosError)
          notify(error?.status || 500, undefined, "error", error);
        else notify("Não foi possível se conectar ao servidor!", "error");
      }
    }

    fetchTables();
  }, [datasource]);

  useEffect(() => {
    async function fetchDataSources() {
      try {
        const response = await api.get("/datasource/findMany");
        setDataSources(response.data);
        const findDataSourceDefault = response.data.find(
          (os: RowsDataSource) => os.default,
        );
        if (findDataSourceDefault) {
          setDatasource(findDataSourceDefault);
        }
      } catch (error) {
        console.error(error);
        if (error instanceof AxiosError)
          notify(error.status || 500, undefined, "error", error);
        else notify("Não foi possível se conectar ao servidor!", "error");
      }
    }
    fetchDataSources();
  }, []);

  const getFilteredRows = useMemo(() => {
    return Object.entries(tables)?.filter(([key]) => {
      return filterTable
        ? key?.toLowerCase().includes(filterTable.toLowerCase())
        : true;
    });
  }, [tables, filterTable]);

  async function executeQuery() {
    if (!query || query.trim() === "") return;
    setLoading(true);
    try {
      const result = await api.post("/filters/query", {
        query,
        ...datasource,
      });
      setResultQuery(result.data);
      setErrorQuery("");
      setLoading(false);
    } catch (error) {
      console.error(error);
      setResultQuery([]);
      setLoading(false);
      if (error instanceof AxiosError && error?.response?.data?.error)
        setErrorQuery(error.response.data.error);
    }
  }

  const handleEditFilter = async () => {
    const errors = {
      variable: !variable.trim(),
      label: !label.trim(),
      column: !column.trim(),
      query: filterType === "select" && !query.trim(),
      datasource: filterType === "select" && !datasource,
    };
    setFieldErrors(errors);

    if (Object.values(errors).some(Boolean)) {
      return;
    }
    try {
      const newFilter: Filter = {
        type: filterType as FilterType,
        label,
        column,
        variable,
        ...(filterType === "select" && datasource && { query, datasource }),
      };
      const filterUpdate = [...pageFilter];
      if (selectFilterIndex) {
        filterUpdate[selectFilterIndex] = newFilter;
        const response = await api.post("/filters/updateFilter", {
          id: pageId,
          filters: filterUpdate,
        });
        notify(
          response.status,
          "Filtro atualizado com sucesso",
          "success",
          null,
        );
      }
      navigate(`/configuracoes/filtros/${pageId}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.log(error.response?.data.error);
        notify(
          error.response?.status || 500,
          error.response?.data.error,
          "error",
          null,
        );
      } else notify("Não foi possível se conectar ao servidor!", "error");
    } finally {
      setIsFilterEditor(false);
    }
  };

  const handleFilterType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as FilterType;
    if (value === "date") {
      setVariable("date");
      setLabel("Data");
      setColumn("data");
    } else {
      setVariable("");
      setLabel("");
      setColumn("");
    }
    setFilterType(value);
  };

  const dynamicColumns = () => {
    // Busca primeiro resultado e extrai as chaves (para usar como field)
    const filteredFields = resultQuery && Object.keys(resultQuery[0]);
    const columns: any[] = [];
    // percorre as chaves e cria colunas dinamicas de acordo com as chaves
    filteredFields?.map((field, index) => {
      columns.push({
        id: index,
        field: field,
        headerName: field,
        flex: 1,
        minWidth: 150,
      });
    });
    return columns;
  };

  const customRows = resultQuery.map((object, index) => ({
    ...object,
    id: index,
  }));

  return (
    filterType && (
      <Box sx={styles.pageContainer}>
        <Box sx={styles.paper}>
          <Box sx={{ m: 3 }}>
            <Box sx={styles.formContainer}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Variável
                  </Typography>
                  <Tooltip title="Esta variável será utilizada para referenciar os filtros nas consultas SQL dos gráficos">
                    <Box sx={{ marginBottom: 0.2 }}>
                      <InfoIcon
                        style={{
                          fill: theme.palette.text.secondary,
                          height: "auto",
                          width: "1rem",
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
                <TextField
                  placeholder="Nome da variável para o SQL"
                  value={variable}
                  error={fieldErrors.variable}
                  helperText={
                    fieldErrors.variable ? "Campo Obrigatorio" : undefined
                  }
                  sx={styles.textfield}
                  onChange={(e) => {
                    setVariable(e.target.value);
                    if (fieldErrors.variable)
                      setFieldErrors({ ...fieldErrors, variable: false });
                  }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Tipo de filtro
                </Typography>
                <TextField
                  select
                  value={filterType}
                  sx={styles.textfield}
                  onChange={(e) =>
                    handleFilterType(e as React.ChangeEvent<HTMLInputElement>)
                  }
                >
                  <MenuItem value={"select"}>Query</MenuItem>
                  <MenuItem value={"date"}>Data</MenuItem>
                </TextField>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Título
                </Typography>
                <TextField
                  placeholder="Título do filtro"
                  value={label}
                  error={fieldErrors.label}
                  helperText={
                    fieldErrors.label ? "Campo Obrigatorio" : undefined
                  }
                  sx={styles.textfield}
                  onChange={(e) => {
                    setLabel(e.target.value);
                    if (fieldErrors.label)
                      setFieldErrors({ ...fieldErrors, label: false });
                  }}
                />
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Coluna
                  </Typography>
                  <Tooltip title="Informe o nome coluna desejada na consulta SQL abaixo">
                    <Box sx={{ marginBottom: 0.2 }}>
                      <InfoIcon
                        style={{
                          fill: theme.palette.text.secondary,
                          height: "auto",
                          width: "1rem",
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
                <TextField
                  placeholder="Nome da coluna"
                  value={column}
                  error={fieldErrors.column}
                  helperText={
                    fieldErrors.column ? "Campo Obrigatorio" : undefined
                  }
                  sx={styles.textfield}
                  onChange={(e) => {
                    setColumn(e.target.value);
                    if (fieldErrors.column)
                      setFieldErrors({ ...fieldErrors, column: false });
                  }}
                />
              </Box>
            </Box>
            {filterType === "select" && (
              <>
                <Box sx={styles.queryContainer}>
                  <Typography variant="h6">Query Options</Typography>
                  <Divider sx={{ py: 0.5 }} />
                </Box>

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ my: 1 }}>
                      Query
                    </Typography>
                  </Box>
                  <QueryBox
                    errorQuery={errorQuery}
                    loading={loading}
                    resultQuery={resultQuery}
                    dynamicColumns={dynamicColumns}
                    customRows={customRows}
                    filterTable={filterTable}
                    setFilterTable={setFilterTable}
                    open={open}
                    setOpen={setOpen}
                    getFilteredRows={getFilteredRows}
                    query={query}
                    setQuery={setQuery}
                    executeQuery={executeQuery}
                    datasources={dataSources}
                    datasource={datasource}
                    setDatasource={setDatasource}
                  />
                </Box>
              </>
            )}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "end",
                marginTop: 3,
                gap: 2,
              }}
            >
              <Button
                sx={{
                  backgroundColor: "transparent",
                  color: theme.palette.cromatic.green.main,
                  minHeight: 36,
                }}
                onClick={() => {
                  setIsFilterEditor(false);
                  navigate(`/configuracoes/filtros/${pageId}`);
                }}
              >
                Cancelar
              </Button>
              <Button
                sx={{
                  backgroundColor: theme.palette.text.primary,
                  color: theme.palette.text.inverted,
                  minHeight: 36,
                }}
                onClick={handleEditFilter}
              >
                Salvar
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  );
}
